import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Award, Search, User, Loader2, Save, RefreshCw, Trash2, CheckCircle2,
  XCircle, Crown, BookOpen, FileText, ScrollText, Flame, Info, Edit3, X,
} from 'lucide-react';
import { toast } from 'sonner';
import BadgeIcon from '../components/BadgeIcons';
import { StreakBulkSetter } from '../components/StreakBulkSetter';

const emptySnapshot = {
  reached_at: '',
  books_count: 0,
  total_pages: 0,
  articles_completed: 0,
  studies_completed: 0,
  streak_baseline: 0,
};

const AdminBadgeProgressPage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState(null);
  const [snap, setSnap] = useState(emptySnapshot);
  const [savingSnap, setSavingSnap] = useState(false);
  const [editKey, setEditKey] = useState(null); // badge key being edited
  const [editValue, setEditValue] = useState(0);
  const [editSaving, setEditSaving] = useState(false);
  const searchTimer = useRef(null);

  const doSearch = async (q) => {
    if (!q || q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get('/admin/badges/search-reader', { params: { q } });
      setResults(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل البحث');
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => doSearch(query), 350);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [query]);

  const loadProgress = async (rn) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/badges/progress/${encodeURIComponent(rn)}`);
      setState(data);
      setSnap({
        reached_at: data.snapshot?.reached_at || '',
        books_count: Number(data.snapshot?.books_count || 0),
        total_pages: Number(data.snapshot?.total_pages || 0),
        articles_completed: Number(data.snapshot?.articles_completed || 0),
        studies_completed: Number(data.snapshot?.studies_completed || 0),
        streak_baseline: Number(data.snapshot?.streak_baseline || 0),
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل تحميل بيانات القارئ');
      setState(null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const rn = new URLSearchParams(window.location.search).get('rn');
    if (rn) {
      setSelected({ reader_number: rn });
      setQuery(rn);
      loadProgress(rn);
    }
  }, []);

  const selectReader = (r) => {
    setSelected(r);
    setResults([]);
    setQuery(`${r.reader_number} — ${r.name || r.full_name || ''}`);
    loadProgress(r.reader_number);
  };

  const grant = async (badgeKey) => {
    try {
      await api.post(`/admin/badges/progress/${encodeURIComponent(selected.reader_number)}/grant/${encodeURIComponent(badgeKey)}`);
      toast.success('تم منح الوسام');
      await loadProgress(selected.reader_number);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل منح الوسام');
    }
  };

  const startEdit = (b) => {
    setEditKey(b.key);
    setEditValue(Number(b.current || 0));
  };

  const cancelEdit = () => { setEditKey(null); setEditValue(0); };

  const saveEdit = async (b) => {
    setEditSaving(true);
    try {
      const { data } = await api.put(
        `/admin/badges/progress/${encodeURIComponent(selected.reader_number)}/override/${encodeURIComponent(b.key)}`,
        { current: Math.max(0, Number(editValue) || 0) },
      );
      toast.success(data?.auto_granted ? 'تم ضبط التقدم ومنح الوسام تلقائياً' : 'تم ضبط التقدم');
      cancelEdit();
      await loadProgress(selected.reader_number);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل ضبط التقدم');
    } finally { setEditSaving(false); }
  };

  const clearOverride = async (b) => {
    if (!window.confirm(`إعادة الحساب الآلي لوسام «${b.name}»؟`)) return;
    try {
      await api.delete(`/admin/badges/progress/${encodeURIComponent(selected.reader_number)}/override/${encodeURIComponent(b.key)}`);
      toast.success('تم الرجوع للحساب الآلي');
      cancelEdit();
      await loadProgress(selected.reader_number);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل الحذف');
    }
  };

  const revoke = async (badgeKey) => {
    if (!window.confirm('سحب هذا الوسام من القارئ؟')) return;
    try {
      await api.delete(`/admin/badges/progress/${encodeURIComponent(selected.reader_number)}/revoke/${encodeURIComponent(badgeKey)}`);
      toast.success('تم سحب الوسام');
      await loadProgress(selected.reader_number);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل سحب الوسام');
    }
  };

  const saveSnapshot = async () => {
    setSavingSnap(true);
    try {
      await api.put(`/admin/badges/progress/${encodeURIComponent(selected.reader_number)}/snapshot`, snap);
      toast.success('تم حفظ السناب شوت');
      await loadProgress(selected.reader_number);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل الحفظ');
    } finally { setSavingSnap(false); }
  };

  const deleteSnapshot = async () => {
    if (!window.confirm('حذف السناب شوت؟ سيعود القارئ إلى حالة "لم يبلغ القمة".')) return;
    try {
      await api.delete(`/admin/badges/progress/${encodeURIComponent(selected.reader_number)}/snapshot`);
      toast.success('تم الحذف');
      await loadProgress(selected.reader_number);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل الحذف');
    }
  };

  const createSnapshotFromNow = async () => {
    if (!state) return;
    const nowIso = new Date().toISOString();
    const fresh = {
      reached_at: nowIso,
      books_count: state.reader.books_count,
      total_pages: state.reader.total_pages,
      articles_completed: state.current_stats.articles_completed,
      studies_completed: state.current_stats.studies_completed,
      streak_baseline: state.current_stats.current_streak,
    };
    setSavingSnap(true);
    try {
      await api.put(`/admin/badges/progress/${encodeURIComponent(selected.reader_number)}/snapshot`, fresh);
      toast.success('تم إنشاء السناب شوت من القيم الحالية');
      await loadProgress(selected.reader_number);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل الإنشاء');
    } finally { setSavingSnap(false); }
  };

  const earnedCount = useMemo(() => state?.badges?.filter(b => b.earned).length || 0, [state]);

  return (
    <div className="space-y-6" data-testid="admin-badge-progress-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-accent flex items-center gap-2">
          <Award className="w-7 h-7 text-amber-500" /> إدارة تقدّم الأوسمة
        </h1>
        <p className="text-muted-foreground text-sm mt-2">
          ابحث عن قارئ لمنح/سحب أوسمته يدوياً أو ضبط سناب شوت القمة (الأساس الذي يُحسب منه التقدم).
        </p>
      </div>

      {/* Search */}
      <Card className="p-4 border-2 border-amber-200 bg-gradient-to-br from-amber-50/40 to-white relative">
        <label className="text-xs text-muted-foreground mb-1 block">ابحث برقم القارئ أو اسمه</label>
        <div className="relative">
          <Search className="absolute right-3 top-3 w-4 h-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelected(null); setState(null); }}
            placeholder="مثال: 41414 أو أوس السيابي"
            className="pr-9"
            data-testid="reader-search-input"
          />
        </div>
        {searching && <Loader2 className="w-4 h-4 animate-spin text-amber-600 absolute left-4 top-11" />}

        {results.length > 0 && !selected && (
          <div className="mt-2 border rounded-xl bg-white shadow-sm max-h-72 overflow-y-auto divide-y">
            {results.map((r) => (
              <button
                key={`${r.gender}-${r.reader_number}`}
                onClick={() => selectReader(r)}
                className="w-full text-right px-3 py-2 hover:bg-amber-50 flex items-center gap-2 transition"
                data-testid={`reader-result-${r.reader_number}`}
              >
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="font-mono text-xs text-muted-foreground">{r.reader_number}</span>
                <span className="text-sm">{r.name || r.full_name || '—'}</span>
                <span className="mr-auto text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                  {r.gender === 'female' ? 'قارئة' : 'قارئ'}
                </span>
              </button>
            ))}
          </div>
        )}
      </Card>

      {loading && (
        <Card className="p-10 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
        </Card>
      )}

      {state && !loading && (
        <>
          {/* Reader summary */}
          <Card className="p-5 border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-white">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold text-accent flex items-center gap-2">
                  <User className="w-5 h-5 text-amber-600" /> {state.reader.name}
                  <span className="font-mono text-xs text-muted-foreground">#{state.reader.reader_number}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                    {state.reader.gender === 'female' ? 'قارئة' : 'قارئ'}
                  </span>
                </h3>
                <div className="flex flex-wrap gap-2 mt-2 text-xs">
                  <span className="flex items-center gap-1 bg-white border border-amber-200 px-2 py-1 rounded-full">
                    <Crown className="w-3.5 h-3.5 text-amber-500" />
                    {state.at_peak ? 'بلغ القمة' : 'لم يبلغ القمة'}
                  </span>
                  <span className="flex items-center gap-1 bg-white border px-2 py-1 rounded-full">
                    <BookOpen className="w-3.5 h-3.5 text-amber-600" /> {state.reader.books_count} كتاب
                  </span>
                  <span className="flex items-center gap-1 bg-white border px-2 py-1 rounded-full">
                    <ScrollText className="w-3.5 h-3.5 text-amber-600" /> {state.reader.total_pages} صفحة
                  </span>
                  <span className="flex items-center gap-1 bg-white border px-2 py-1 rounded-full">
                    <FileText className="w-3.5 h-3.5 text-amber-600" /> {state.current_stats.articles_completed} مقالة
                  </span>
                  <span className="flex items-center gap-1 bg-white border px-2 py-1 rounded-full">
                    <FileText className="w-3.5 h-3.5 text-amber-600" /> {state.current_stats.studies_completed} دراسة
                  </span>
                  <span className="flex items-center gap-1 bg-white border px-2 py-1 rounded-full">
                    <Flame className="w-3.5 h-3.5 text-rose-500" /> سلسلة حالية {state.current_stats.current_streak}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[11px] text-muted-foreground">الأوسمة المُنالة</p>
                <p className="text-2xl font-bold text-amber-600">{earnedCount} / {state.badges.length}</p>
              </div>
            </div>
          </Card>

          {/* Snapshot editor */}
          <Card className="p-5 border-2 border-amber-200">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="text-lg font-bold text-accent flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-600" /> سناب شوت القمة (Baseline)
              </h3>
              {state.snapshot ? (
                <Button variant="ghost" size="sm" onClick={deleteSnapshot} className="text-rose-600 hover:bg-rose-50 gap-1" data-testid="delete-snapshot-btn">
                  <Trash2 className="w-4 h-4" /> حذف السناب شوت
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={createSnapshotFromNow} disabled={savingSnap} className="gap-1" data-testid="create-snapshot-btn">
                  {savingSnap ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  إنشاؤه من القيم الحالية
                </Button>
              )}
            </div>

            <div className="flex items-start gap-1.5 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                تقدّم الأوسمة يُحسب من تاريخ بلوغ القمة: أوسمة الاستمرار = عدد أيام النشاط منذ هذا التاريخ (يوم الانقطاع لا يُحسب فقط، ولا يُصفَّر العدّاد)، وبقية الأوسمة = الفرق بين القيم الحالية وهذه القيم الأساسية.
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">تاريخ بلوغ القمة (ISO)</label>
                <Input
                  value={snap.reached_at || ''}
                  onChange={(e) => setSnap({ ...snap, reached_at: e.target.value })}
                  placeholder="YYYY-MM-DDTHH:mm:ss.sssZ"
                  dir="ltr" className="font-mono text-xs"
                  data-testid="snap-reached-at"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">أساس الكتب</label>
                <Input type="number" value={snap.books_count}
                  onChange={(e) => setSnap({ ...snap, books_count: Number(e.target.value) || 0 })}
                  data-testid="snap-books" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">أساس الصفحات</label>
                <Input type="number" value={snap.total_pages}
                  onChange={(e) => setSnap({ ...snap, total_pages: Number(e.target.value) || 0 })}
                  data-testid="snap-pages" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">أساس المقالات</label>
                <Input type="number" value={snap.articles_completed}
                  onChange={(e) => setSnap({ ...snap, articles_completed: Number(e.target.value) || 0 })}
                  data-testid="snap-articles" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">أساس الدراسات</label>
                <Input type="number" value={snap.studies_completed}
                  onChange={(e) => setSnap({ ...snap, studies_completed: Number(e.target.value) || 0 })}
                  data-testid="snap-studies" />
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <Button onClick={saveSnapshot} disabled={savingSnap} className="bg-amber-600 hover:bg-amber-700 text-white gap-1" data-testid="save-snapshot-btn">
                {savingSnap ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                حفظ السناب شوت
              </Button>
              {!state.snapshot && (
                <span className="text-[11px] text-muted-foreground">لا يوجد سناب شوت بعد — سيُنشأ عند الحفظ.</span>
              )}
            </div>
          </Card>

          {/* Badges grid */}
          <StreakBulkSetter
            readerNumber={selected.reader_number}
            initial={state.badges.find((b) => b.condition_type === 'streak_days')?.current || 0}
            onDone={() => loadProgress(selected.reader_number)}
          />
          <div>
            <h3 className="text-lg font-bold text-accent flex items-center gap-2 mb-3">
              <Award className="w-5 h-5 text-amber-500" /> الأوسمة
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {state.badges.map((b) => (
                <Card
                  key={b.key}
                  className={`p-4 border-2 transition-all ${b.earned ? 'border-amber-400 bg-gradient-to-br from-amber-50 to-white shadow' : 'border-muted'}`}
                  data-testid={`progress-badge-${b.key}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-14 h-14 shrink-0 flex items-center justify-center ${b.earned ? '' : 'opacity-40 grayscale'}`}>
                      <BadgeIcon name={b.icon} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <h4 className="font-bold text-accent">{b.name}</h4>
                        {b.earned && <CheckCircle2 className="w-4 h-4 text-amber-500" />}
                        {b.override != null && (
                          <span className="text-[9px] bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded-full" title="تقدّم مضبوط يدوياً">يدوي</span>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{b.description}</p>
                      <div className="mt-2">
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className="h-full bg-amber-500 transition-all" style={{ width: `${b.progress_percent}%` }} />
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {b.current} / {b.target} ({b.progress_percent}%)
                          {b.override != null && (
                            <span className="mx-1 text-[9px]" data-testid={`override-info-${b.key}`}>
                              · ضُبط على {b.override} بتاريخ {b.override_set_date} ويكمل تلقائياً · الحساب الآلي: {b.computed}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  {editKey === b.key ? (
                    <div className="mt-3 p-3 bg-amber-50/60 border border-amber-200 rounded-lg space-y-2" data-testid={`edit-panel-${b.key}`}>
                      <label className="text-[11px] text-muted-foreground">قيمة اليوم (الهدف: {b.target}) — يكمل العدّ منها تلقائياً بعد اليوم</label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number" min="0"
                          value={editValue}
                          onChange={(e) => setEditValue(Number(e.target.value) || 0)}
                          className="h-9"
                          data-testid={`edit-value-${b.key}`}
                        />
                        <Button size="sm" onClick={() => saveEdit(b)} disabled={editSaving} className="bg-amber-600 hover:bg-amber-700 text-white gap-1 shrink-0" data-testid={`save-edit-${b.key}`}>
                          {editSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={cancelEdit} className="shrink-0" data-testid={`cancel-edit-${b.key}`}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      {b.override != null && (
                        <Button size="sm" variant="ghost" onClick={() => clearOverride(b)} className="text-[11px] text-muted-foreground hover:text-rose-600 gap-1 h-7 px-2" data-testid={`clear-override-${b.key}`}>
                          <RefreshCw className="w-3 h-3" /> إرجاع الحساب الآلي
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => startEdit(b)} className="gap-1 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid={`edit-badge-${b.key}`}>
                        <Edit3 className="w-3.5 h-3.5" /> تعديل
                      </Button>
                      {b.earned ? (
                        <Button size="sm" variant="ghost" onClick={() => revoke(b.key)} className="gap-1 text-rose-600 hover:bg-rose-50" data-testid={`revoke-badge-${b.key}`}>
                          <XCircle className="w-3.5 h-3.5" /> سحب
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => grant(b.key)} className="gap-1 text-amber-700 hover:bg-amber-50" data-testid={`grant-badge-${b.key}`}>
                          <CheckCircle2 className="w-3.5 h-3.5" /> منح
                        </Button>
                      )}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        </>
      )}

      {!state && !loading && !selected && (
        <Card className="p-10 text-center text-muted-foreground border-dashed">
          ابدأ بالبحث عن قارئ لعرض أوسمته وتقدّمه.
        </Card>
      )}
    </div>
  );
};

export default AdminBadgeProgressPage;
